import { ModalAssistant } from '@mairistem/assistant';
import { Form } from '@mairistem/composants';
import type { Gabarit } from '@mairistem/finances-utils';
import type { DropdownOption } from '@mairistem/liste';
import { isNumber } from 'lodash';
import React from 'react';
import EtapeCaracteristique from '@/src/components/SubventionFichePanel/ConditionsVersements/AssistantVersement/EtapeCaracteristique';
import EtapeJustificatif from '@/src/components/SubventionFichePanel/ConditionsVersements/AssistantVersement/EtapeJustificatif';
import EtapeModaliteAttribution from '@/src/components/SubventionFichePanel/ConditionsVersements/AssistantVersement/EtapeModaliteAttribution';
import type { ConditionVersement } from '@/src/interfaces/conditionVersement';
import type { Step } from '@/src/interfaces/step';
import {
	validateStepCaracteristique,
	validateStepModalite,
} from '@/src/utils/conditionVersement';

export const STEP_CARACTERISTIQUE = 0;
export const STEP_MODALITE_ATTRIBUTION = 1;
export const STEP_JUSTIFICATIF = 2;

interface AssistantVersementsProps {
	conditionVersement: ConditionVersement;
	montantPrecedentesConditions: number;
	budgetOptions: DropdownOption[];
	open: boolean;
	onFinish: (condition: ConditionVersement) => void;
	// sert à mettre à jour le state directement sans validation de l'utilisateur
	onClose: () => void;
	onLibelleDatalist: () => string[];
	step: number;
	montantSubvention: number;
	gabaritsPj: Gabarit[];
	setStep: React.Dispatch<React.SetStateAction<number>>;
}

const DEFAULT_STEPS = [
	{
		dataTestId: 'boutonCaracteristiquesStep',
		required: true,
		title: 'Caractéristiques',
		updating: false,
		validated: false,
	},
	{
		dataTestId: 'boutonModalitesAttributionStep',
		required: true,
		title: "Modalités d'attribution",
		updating: false,
		validated: false,
	},
	{
		dataTestId: 'boutonJustificatifsStep',
		required: false,
		title: 'Justificatifs à fournir',
		updating: false,
		validated: false,
	},
];

const AssistantVersements = ({
	conditionVersement = null,
	montantPrecedentesConditions = 0,
	budgetOptions = null,
	open = false,
	onFinish = null,
	onClose = null,
	step = 0,
	montantSubvention = 0,
	onLibelleDatalist = null,
	gabaritsPj = null,
	setStep,
}: AssistantVersementsProps) => {
	const [steps, setSteps] = React.useState<Step[]>(DEFAULT_STEPS);
	const [versement, setVersement] =
		React.useState<ConditionVersement>(conditionVersement);

	const initSteps = React.useCallback(() => {
		const isConditionValidated =
			conditionVersement?.isValidated ||
			isNumber(conditionVersement?.identifiant);

		setSteps((oldSteps) =>
			oldSteps.map((oldStep) => ({
				...oldStep,
				error: null,
				validated: isConditionValidated,
			})),
		);
	}, [conditionVersement?.identifiant, conditionVersement?.isValidated]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: On a besoin de 'conditionVersement?.identifiant'
	React.useEffect(() => {
		initSteps();
	}, [conditionVersement?.identifiant, initSteps]);

	const handleSaveStep = (index) => {
		let stepResult = null;
		switch (index) {
			case STEP_CARACTERISTIQUE:
				stepResult = validateStepCaracteristique(versement);
				break;
			case STEP_MODALITE_ATTRIBUTION:
				stepResult = validateStepModalite(
					montantPrecedentesConditions,
					versement?.montant,
					versement?.dateButoire,
					montantSubvention,
				);
				break;
			default:
				return true;
		}
		stepResult.updating = false;

		setSteps((oldSteps) =>
			oldSteps.map((oldStep, i) => {
				if (i !== index) {
					return oldStep;
				}

				return { ...oldStep, ...stepResult };
			}),
		);

		return stepResult.validated;
	};

	const handleFinish = React.useCallback(() => {
		const isCaracteristiqueValid = steps[STEP_CARACTERISTIQUE].validated;
		const isAttributionValid = steps[STEP_CARACTERISTIQUE].validated;

		if (!isCaracteristiqueValid) {
			setStep(STEP_CARACTERISTIQUE);
		}

		if (!isAttributionValid) {
			setStep(STEP_MODALITE_ATTRIBUTION);
		}

		if (isAttributionValid && isCaracteristiqueValid) {
			onFinish?.({
				...versement,
				isValidated: true, // Permet de savoir que les étapes ont déjà été validées
			});
		}
	}, [steps, setStep, onFinish, versement]);

	const handleCurrentStepChange = (newStep) => {
		handleSaveStep(step);
		setStep(newStep);
	};

	const handleChange = (condition) => {
		setSteps((oldSteps) =>
			oldSteps.map((oldStep, i) => {
				if (i === step) {
					return {
						...oldStep,
						updating: true,
						validated: false,
					};
				}

				return oldStep;
			}),
		);

		setVersement((old) => ({
			...old,
			...condition,
		}));
	};

	const handleClose = () => {
		initSteps();
		onClose?.();
	};

	return (
		<ModalAssistant
			open={open}
			title="Versement"
			steps={steps}
			heightContent={window.innerHeight * 0.6}
			onFinish={handleFinish}
			onStepChange={handleCurrentStepChange}
			onSaveStep={handleSaveStep}
			onClose={handleClose}
			currentStep={step}
			initialStep={STEP_CARACTERISTIQUE}
			data-testid="conditionVersementModal"
			autoUpdating={false}
		>
			<ModalAssistant.Content>
				<Form>
					{step === STEP_CARACTERISTIQUE && (
						<EtapeCaracteristique
							conditionVersement={versement}
							montantPrecedentesConditions={montantPrecedentesConditions}
							onLibelleDatalist={onLibelleDatalist}
							montantSubvention={montantSubvention}
							onChange={handleChange}
							budgetOptions={budgetOptions}
						/>
					)}

					{step === STEP_MODALITE_ATTRIBUTION && (
						<EtapeModaliteAttribution
							conditionVersement={versement}
							onChange={handleChange}
							montantPrecedentesConditions={montantPrecedentesConditions}
							montantSubvention={montantSubvention}
						/>
					)}

					{step === STEP_JUSTIFICATIF && (
						<EtapeJustificatif
							naturesConditions={versement?.naturesConditions}
							deletedNaturesConditions={versement?.deletedNaturesConditions}
							onChange={handleChange}
							gabaritsPj={gabaritsPj}
						/>
					)}
				</Form>
			</ModalAssistant.Content>
		</ModalAssistant>
	);
};

export default AssistantVersements;
